import { supabase } from './client';

export type StrainRow = {
  id: string;
  name: string;
  brand: string;
  genetics: string | null;
  category: 'Flower' | 'Oil' | 'Cartridge';
  slug: string;
  lineage: 'sativa' | 'indica' | 'hybrid' | null;
};

export type BatchRow = {
  id: string;
  strain_id: string;
  batch_number: string;
  myrcene_pct: number | null;
  limonene_pct: number | null;
  linalool_pct: number | null;
  caryophyllene_pct: number | null;
  pinene_pct: number | null;
  humulene_pct: number | null;
  official_coa_url: string | null;
  verified_at: string | null;
};

export type IrradiationKind = 'none' | 'e-beam' | 'beta' | 'gamma';

export type StockRow = {
  pharmacy_slug: string;
  status: 'In Stock' | 'Low Stock' | 'Out of Stock';
  last_checked: string;
  thc_pct_advertised: number | null;
  cbd_pct_advertised: number | null;
  // Migration 0021 — render with a "<" prefix when true.
  thc_pct_is_upper_bound: boolean;
  cbd_pct_is_upper_bound: boolean;
  irradiation: IrradiationKind | null;
  price_per_gram_gbp: number | null;
};

export const IRRADIATION_LABELS: Record<IrradiationKind, string> = {
  'none': 'Non-irradiated',
  'e-beam': 'E-Beam',
  'beta': 'Beta',
  'gamma': 'Gamma',
};

/**
 * Format a cannabinoid percentage for display, preserving "<" when the
 * source value was an upper bound. Returns '' for null.
 */
export function formatPct(value: number | null, isUpperBound: boolean): string {
  if (value == null) return '';
  return isUpperBound ? `<${value}%` : `${value}%`;
}

export type SearchResult = {
  strain_id: string;
  strain_name: string;
  brand: string;
  batch_id: string;
  batch_number: string;
  score: number;
  is_historical: boolean;
  in_stock: boolean;
};

export async function fetchStrains(): Promise<StrainRow[]> {
  // `excluded_from_listing` ships in migration 0013. Use .or() so the
  // filter works against both old (column missing) and new DBs without
  // needing a conditional. `not.is.true` equals "false or null".
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data, error } = await (supabase as any)
    .from('strains')
    .select('id, name, brand, genetics, category, slug, lineage')
    .not('excluded_from_listing', 'is', true)
    .order('brand');
  if (error) throw error;
  return (data ?? []) as unknown as StrainRow[];
}

export async function fetchStrainBySlug(slug: string): Promise<StrainRow | null> {
  const { data, error } = await supabase
    .from('strains')
    .select('id, name, brand, genetics, category, slug, lineage')
    .eq('slug', slug)
    .maybeSingle();
  if (error) throw error;
  return (data as unknown as StrainRow) ?? null;
}

export async function fetchBatchesForStrain(strainId: string): Promise<BatchRow[]> {
  const { data, error } = await supabase
    .from('batches')
    .select(
      'id, strain_id, batch_number, myrcene_pct, limonene_pct, linalool_pct, caryophyllene_pct, pinene_pct, humulene_pct, official_coa_url, verified_at',
    )
    .eq('strain_id', strainId)
    .order('verified_at', { ascending: false, nullsFirst: false });
  if (error) throw error;
  return (data ?? []) as unknown as BatchRow[];
}

export async function fetchStockForStrain(strainId: string): Promise<StockRow[]> {
  const { data, error } = await supabase
    .from('stock_status')
    .select(
      'pharmacy_slug, status, last_checked, thc_pct_advertised, cbd_pct_advertised, thc_pct_is_upper_bound, cbd_pct_is_upper_bound, irradiation, price_per_gram_gbp',
    )
    .eq('strain_id', strainId);
  if (error) throw error;
  return (data ?? []) as unknown as StockRow[];
}

export type LiveStockEntry = {
  strain: StrainRow;
  pharmacy_slug: string;
  pharmacy_name: string;
  status: 'In Stock' | 'Low Stock' | 'Out of Stock';
  last_checked: string;
  thc_pct_advertised: number | null;
  cbd_pct_advertised: number | null;
  thc_pct_is_upper_bound: boolean;
  cbd_pct_is_upper_bound: boolean;
  irradiation: IrradiationKind | null;
  price_per_gram_gbp: number | null;
};

export type PharmacyRow = { slug: string; name: string };

export async function fetchPharmacies(): Promise<PharmacyRow[]> {
  const { data, error } = await supabase
    .from('pharmacies')
    .select('slug, name')
    .order('name');
  if (error) throw error;
  return (data ?? []) as unknown as PharmacyRow[];
}

/**
 * Live stock feed — every tracked strain across all pharmacies, including
 * Out of Stock rows. The UI applies the "In Stock Only" toggle client-side
 * so patients can flip it without another round-trip. Sorted by most
 * recently updated.
 */
export async function fetchLiveStock(): Promise<LiveStockEntry[]> {
  const { data, error } = await supabase
    .from('stock_status')
    .select(
      'pharmacy_slug, status, last_checked, thc_pct_advertised, cbd_pct_advertised, thc_pct_is_upper_bound, cbd_pct_is_upper_bound, irradiation, price_per_gram_gbp, strain:strains(id, name, brand, genetics, category, slug, lineage)',
    )
    .order('last_checked', { ascending: false })
    .limit(2000);
  if (error) throw error;

  const rows = (data ?? []) as unknown as Array<
    Omit<LiveStockEntry, 'pharmacy_name' | 'strain'> & { strain: StrainRow }
  >;
  // Enrich with pharmacy display name via a second tiny fetch. Cheap: only
  // 6 rows today.
  const pharmacies = await fetchPharmacies();
  const nameBySlug = new Map(pharmacies.map((p) => [p.slug, p.name]));
  return rows.map((r) => ({
    ...r,
    pharmacy_name: nameBySlug.get(r.pharmacy_slug) ?? r.pharmacy_slug,
  }));
}

export async function searchStrains(args: {
  symptomSlug: string;
  inStockOnly?: boolean;
  includeHistorical?: boolean;
  irradiationKinds?: IrradiationKind[] | null;
  limit?: number;
}): Promise<SearchResult[]> {
  // Null = no irradiation filter. Empty array = filter that matches nothing
  // (not what callers typically want), so coerce empty → null.
  const kinds =
    args.irradiationKinds && args.irradiationKinds.length > 0
      ? args.irradiationKinds
      : null;
  // Call .rpc() as a method on the client (not via a detached reference)
  // — postgrest-js reads `this.rest` internally, and the previous
  // `const rpc = supabase.rpc` pattern stripped that `this` binding,
  // which blew up under SSR with "Cannot read properties of undefined
  // (reading 'rest')". `as any` on the client sidesteps the strongly
  // typed RPC overloads that the generated Database types don't cover.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data, error } = await (supabase as any).rpc('search_strains', {
    p_symptom_slug: args.symptomSlug,
    p_in_stock_only: args.inStockOnly ?? true,
    p_include_historical: args.includeHistorical ?? false,
    p_irradiation_kinds: kinds,
    p_limit: args.limit ?? 50,
  });
  if (error) throw error;
  return (data ?? []) as unknown as SearchResult[];
}
